import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Paper, Button, TextField, Chip,
  IconButton, LinearProgress, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Stack, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Switch, FormControlLabel, Skeleton, Checkbox,
  useTheme, alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import SearchIcon from '@mui/icons-material/SearchRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useAdminScreen } from './useAdminScreen';
import {
  tagChipSx, mutedChipSx, statusChipSx, statusTone,
  consoleCardSx, consoleCardHoverSx, microLabelSx,
} from './consoleChips';

// Shared table-head treatment: micro-labels on every console table.
const tableHeadSx = { '& th': { ...microLabelSx, py: 1.25 } };

const ROLE_LABELS = { orgAdmin: 'Org Admin', billingAdmin: 'Billing Admin', member: 'Member' };

// Dialog buttons must stay inside the 256px MUI dialog available at a 320px
// viewport. Keep DOM and visual order aligned, stack actions on phones, and
// restore the standard right-aligned row at the `sm` breakpoint.
export const responsiveDialogActionsSx = {
  px: 3,
  pb: 2,
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: { xs: 1, sm: 0 },
  '& > :not(style)': { width: { xs: '100%', sm: 'auto' } },
  '& > :not(style) ~ :not(style)': { marginLeft: { xs: '0 !important', sm: '8px' } },
};

export const isCoreOrgLoadBlocked = (s) => Boolean(
  s?.canAccessAdmin && s?.coreDataLoaded === false && !s?.loading
);

// Per-action pending state. `s.busy` still drives the page-level progress bar,
// but a control only goes into its pending state when it is the action actually
// in flight: one role change must not freeze the whole roster. Pass `key` to
// narrow the match to one row (a member id, an invited email, a booster pack).
export const isActionPending = (s, action, key) => Boolean(
  s?.busy && s?.pendingAction === action && (key === undefined || s?.pendingKey === key)
);

// True while ANY mutation targeting this row is in flight, so a row's controls
// lock together without touching the rest of the table.
export const isRowPending = (s, key) => Boolean(
  s?.busy && key != null && s?.pendingKey === key
);

export function CoreOrgLoadErrorState({ s, sx = {} }) {
  return (
    <Alert
      severity="error"
      sx={sx}
      action={(
        <Button color="inherit" size="small" onClick={() => s.refresh()} disabled={s.loading}>
          Retry
        </Button>
      )}
    >
      <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
        {s.error || 'Organization data is temporarily unavailable.'}
      </Box>
      <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
        Restack is not showing seats, members, or credit balances until the data can be verified.
      </Box>
    </Alert>
  );
}

export function StatCard({ label, value, sub, accent, onClick, icon: Icon, meter, meterWarn }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={onClick ? `${label}: ${value}` : undefined}
      sx={{
        ...consoleCardSx(theme),
        flex: 1,
        minWidth: 160,
        font: 'inherit',
        textAlign: 'left',
        color: 'inherit',
        width: '100%',
        ...(onClick && consoleCardHoverSx(theme)),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={microLabelSx}>
          {label}
        </Typography>
        {Icon && <Icon sx={{ fontSize: 18, color: 'text.disabled' }} />}
      </Stack>
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', color: accent || 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {sub && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{sub}</Typography>}
      {meter != null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, meter))}
          sx={{
            height: 6,
            borderRadius: 3,
            mt: 1.25,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            ...(meterWarn && { '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.warning.main } }),
          }}
        />
      )}
    </Paper>
  );
}

// Centralized M365 deployment is the IT half of onboarding. The billing admin
// usually is not the IT admin, so this card exists to be FORWARDED: manifest
// link + the security brief their IT will ask for anyway.
export function DeployAddInCard() {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ ...consoleCardSx(theme), mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Deploy the PowerPoint add-in to your firm</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Your IT admin pushes Restack to everyone at once: M365 Admin Center → Settings → Integrated apps →
        Upload custom apps → Office Add-in, using the manifest below. Scope it to a security group if you
        want to start with one team. Forward this card and the security brief to IT.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small" variant="outlined" endIcon={<OpenInNewIcon fontSize="small" />}
          component="a" href="https://re-stack.com/manifest.xml" target="_blank" rel="noopener noreferrer"
        >
          Add-in manifest
        </Button>
        <Button
          size="small" variant="outlined" endIcon={<OpenInNewIcon fontSize="small" />}
          component="a" href="/Restack-Security-Brief.pdf" target="_blank" rel="noopener noreferrer"
        >
          Security brief for IT
        </Button>
      </Stack>
    </Paper>
  );
}

// First-run checklist after checkout (/admin?welcome=1): the three steps that
// take a fresh buyer from "paid" to "team working in PowerPoint".
export function WelcomeChecklist({ s, onDismiss }) {
  const theme = useTheme();
  const steps = [
    { label: 'Invite your team', detail: 'Each seat you bought is an email invite away (Members tab).', action: () => s.setTab('seats') },
    { label: 'Deploy the Office add-ins', detail: 'Forward the manifest and security brief from Dashboard Resources to IT.', action: null },
    { label: 'Run your first task', detail: 'Open PowerPoint, Excel, or Word, sign in to Restack, and ask it to improve your file.', action: null },
  ];
  return (
    <Paper
      elevation={0}
      sx={{
        ...consoleCardSx(theme),
        mb: 3,
        backgroundColor: alpha(theme.palette.text.primary, 0.025),
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          You're set up. Three steps to a working team:
        </Typography>
        <IconButton size="small" onClick={onDismiss} aria-label="Dismiss checklist"><CloseIcon fontSize="small" /></IconButton>
      </Stack>
      <Stack spacing={1}>
        {steps.map((step, i) => (
          <Stack key={step.label} direction="row" spacing={1.25} alignItems="flex-start">
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main', mt: '2px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {i + 1}. {step.action ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={step.action}
                    sx={{ p: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit', fontWeight: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {step.label}
                  </Box>
                ) : step.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{step.detail}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export function SeatsTab({ s }) {
  const theme = useTheme();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [roleMember, setRoleMember] = useState(null);
  const [roleDraft, setRoleDraft] = useState([]);
  const [billingContactCandidate, setBillingContactCandidate] = useState(null);
  const [deactivateMember, setDeactivateMember] = useState(null);
  const [inviteToCancel, setInviteToCancel] = useState(null);
  const [domainToRemove, setDomainToRemove] = useState(null);
  const seatsUsed = s.org ? s.org.memberCount : 0;
  const seatsTotal = s.org ? s.org.purchasedSeats : 0;
  const pendingSeatCount = s.pendingInvites?.length || 0;
  const seatsReserved = seatsUsed + pendingSeatCount;
  const seatsLeft = Math.max(0, seatsTotal - seatsReserved);
  const editingBillingContact = !!roleMember && s.org?.billingContactUid === roleMember.userId;

  // Pending flags, one per action. Each control (and each dialog) reads only its
  // own flag, so an in-flight role change no longer disables the whole console,
  // and a dialog still cannot be dismissed while ITS mutation is running.
  const savingRoles = !!roleMember && isActionPending(s, 'setMemberRoles', roleMember.userId);
  const transferringContact = !!billingContactCandidate
    && isActionPending(s, 'setBillingContact', billingContactCandidate.userId);
  const deactivating = !!deactivateMember && isActionPending(s, 'deactivateMember', deactivateMember.userId);
  const cancellingInvite = !!inviteToCancel && isActionPending(s, 'cancelInvite', inviteToCancel);
  const removingDomain = isActionPending(s, 'setDomains');
  const sendingInvite = isActionPending(s, 'inviteMember');
  const togglingConductor = isActionPending(s, 'setConductor');
  const togglingClaudeViaOpencode = isActionPending(s, 'setClaudeViaOpencode');

  const openRoleEditor = (member) => {
    setRoleMember(member);
    setRoleDraft((member.roles || []).filter((role) => role === 'orgAdmin' || role === 'billingAdmin'));
  };
  const toggleRoleDraft = (role) => {
    setRoleDraft((current) => current.includes(role)
      ? current.filter((item) => item !== role)
      : [...current, role]);
  };
  const saveRoles = async () => {
    if (!roleMember) return;
    const ok = await s.setMemberRoles(roleMember.userId, roleDraft.length ? roleDraft : ['member']);
    if (ok) setRoleMember(null);
  };
  const transferBillingContact = async () => {
    if (!billingContactCandidate) return;
    const member = billingContactCandidate;
    const label = member.name || member.email || 'Member';
    const ok = await s.setBillingContact(member.userId, label);
    if (ok) {
      setBillingContactCandidate(null);
      setRoleMember(null);
    }
  };

  // Toolbar search filters the roster client-side (rosters are 1-5 seats on
  // Pro; Practice stays comfortably filterable in memory too).
  const q = memberQuery.trim().toLowerCase();
  const matches = (...fields) => !q || fields.some((f) => (f || '').toLowerCase().includes(q));
  const visibleMembers = s.members.filter((m) => matches(m.name, m.email, m.userId));
  const visibleInvites = (s.pendingInvites || []).filter((inv) => matches(inv.email));

  const closeInvite = () => { setInviteOpen(false); setInviteEmail(''); };
  const sendInvite = async () => {
    const ok = await s.inviteMember(inviteEmail.trim().toLowerCase());
    if (ok) closeInvite();
  };

  // Cross-app connector: ON by default (default-on semantics). It is ON unless
  // the org explicitly stored entitlements.conductor === false. The Switch uses
  // optimistic local state so the toggle feels instant, then reverts on error.
  const conductorOn = s.org?.conductor !== false;
  const [conductorChecked, setConductorChecked] = useState(conductorOn);
  useEffect(() => { setConductorChecked(conductorOn); }, [conductorOn]);
  const toggleConductor = async (next) => {
    setConductorChecked(next); // optimistic
    const ok = await s.setConductor(next);
    if (!ok) setConductorChecked(!next); // revert on failure
  };

  // Claude uses the shared opencode loop unless the org explicitly opts back
  // into the retained Claude Agent SDK loop.
  const storedClaudeViaOpencode = s.org?.claudeViaOpencode;
  const claudeViaOpencodeOn = storedClaudeViaOpencode == null
    ? true
    : storedClaudeViaOpencode === true;
  const [claudeViaOpencodeChecked, setClaudeViaOpencodeChecked] = useState(claudeViaOpencodeOn);
  useEffect(() => { setClaudeViaOpencodeChecked(claudeViaOpencodeOn); }, [claudeViaOpencodeOn]);
  const toggleClaudeViaOpencode = async (next) => {
    setClaudeViaOpencodeChecked(next);
    const ok = await s.setClaudeViaOpencode(next);
    if (!ok) setClaudeViaOpencodeChecked(!next);
  };

  // Domain-based joining remains disabled until ownership can be verified.
  // Existing claims can only be removed from this surface.
  const domains = Array.isArray(s.org?.domains) ? s.org.domains : [];
  const removeDomain = async () => {
    if (!domainToRemove) return;
    const ok = await s.setDomains(domains.filter((item) => item !== domainToRemove));
    if (ok) setDomainToRemove(null);
  };

  return (
    <Box>
      {/* One stat card: seats used with the fill meter. (The old "Pending
          invites" count card was double-shown data - the invites themselves
          are rows in the table below, and the "available" sub already nets
          them out.) */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <StatCard
          label="Seats reserved"
          value={`${seatsReserved} / ${seatsTotal}`}
          sub={pendingSeatCount > 0
            ? `${seatsUsed} active · ${pendingSeatCount} pending · ${seatsLeft} available`
            : `${seatsUsed} active · ${seatsLeft} available`}
          meter={seatsTotal ? (seatsReserved / seatsTotal) * 100 : 0}
          meterWarn={seatsLeft === 0}
        />
      </Stack>

      {/* Toolbar: search on the left, the primary action on the right. The
          invite form itself lives in a dialog (the reference table pattern). */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 1.5 }}
      >
        <TextField
          size="small"
          placeholder="Search members"
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 'none', sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search members' }}
        />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        {s.isOrgAdmin && (
          <Button
            variant="contained"
            disableElevation
            disabled={sendingInvite}
            startIcon={<PersonAddAltRoundedIcon />}
            onClick={() => setInviteOpen(true)}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Invite member
          </Button>
        )}
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={consoleCardSx(theme, { padded: false })}>
        <Table size="small">
          <TableHead sx={tableHeadSx}>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Credits this cycle</TableCell>
              {s.isOrgAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleMembers.map((m) => (
              <TableRow key={m.userId} hover>
                <TableCell>
                  {/* Reserve two lines and center so a name-only row is the
                      same height as a name+email row (uniform row rhythm). */}
                  <Box sx={{ minHeight: 38, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>{m.name || m.email || m.userId}</Typography>
                    {m.name && m.email && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{m.email}</Typography>}
                  </Box>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(m.roles || []).filter((role) => role === 'orgAdmin' || role === 'billingAdmin').map((role) => (
                      <Chip key={role} size="small" label={ROLE_LABELS[role]} variant="outlined" sx={tagChipSx(theme)} />
                    ))}
                    {!(m.roles || []).some((role) => role === 'orgAdmin' || role === 'billingAdmin') && (
                      <Chip size="small" label="Member" variant="outlined" sx={mutedChipSx(theme)} />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small" label={m.status === 'deactivated' ? 'Deactivated' : 'Active'}
                    variant="outlined"
                    sx={statusChipSx(theme, m.status === 'deactivated' ? 'neutral' : 'success')}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {Number(m.creditsConsumedThisCycle || 0).toLocaleString()}
                  </Typography>
                </TableCell>
                {s.isOrgAdmin && (
                  /* Row-scoped pending: only the row being acted on locks and
                     spins. Every other row stays live. */
                  <TableCell align="right">
                    <Tooltip title={m.status === 'deactivated' ? 'Deactivated members cannot be edited' : 'Manage roles'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isRowPending(s, m.userId) || m.status === 'deactivated'}
                          onClick={() => openRoleEditor(m)}
                          aria-label={`Manage roles for ${m.email || m.name || 'member'}`}
                        >
                          {isActionPending(s, 'setMemberRoles', m.userId)
                            ? <CircularProgress size={16} />
                            : <ManageAccountsRoundedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {m.status !== 'deactivated' && (
                      <Tooltip title="Deactivate organization license">
                        <span>
                          <IconButton
                            size="small"
                            disabled={isRowPending(s, m.userId)}
                            onClick={() => setDeactivateMember(m)}
                            aria-label={`Deactivate ${m.email || m.name || 'member'}`}
                          >
                            {isActionPending(s, 'deactivateMember', m.userId)
                              ? <CircularProgress size={16} />
                              : <PersonOffRoundedIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {visibleInvites.map((inv) => (
              <TableRow key={`pending-${inv.email}`} sx={{ opacity: 0.7 }}>
                <TableCell>
                  <Box sx={{ minHeight: 38, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{inv.email}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Chip size="small" label="Member" variant="outlined" sx={mutedChipSx(theme)} /></TableCell>
                <TableCell><Chip size="small" label="Invited" variant="outlined" sx={statusChipSx(theme, 'warning')} /></TableCell>
                <TableCell align="right">—</TableCell>
                {s.isOrgAdmin && (
                  <TableCell align="right">
                    <Tooltip title="Cancel invite (frees the seat)">
                      <span>
                        <IconButton
                          size="small"
                          disabled={isRowPending(s, inv.email)}
                          onClick={() => setInviteToCancel(inv.email)}
                          aria-label={`Cancel invitation for ${inv.email}`}
                        >
                          {isActionPending(s, 'cancelInvite', inv.email)
                            ? <CircularProgress size={16} />
                            : <CloseIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {visibleMembers.length === 0 && visibleInvites.length === 0 && (
              <TableRow>
                <TableCell colSpan={s.isOrgAdmin ? 5 : 4}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    {q ? `No members match "${memberQuery.trim()}".` : 'No members yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Domain-based seat claims are paused until ownership verification and
          global uniqueness are enforced. Invite-only is the safe default. */}
      <Paper elevation={0} sx={{ ...consoleCardSx(theme), mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Member access</Typography>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          New members join by email invitation. Domain-based joining is paused until Restack can verify domain ownership.
        </Alert>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          {domains.map((d) => (
            <Chip
              key={d}
              label={d}
              size="small"
              variant="outlined"
              onDelete={s.isOrgAdmin && !removingDomain ? () => setDomainToRemove(d) : undefined}
              sx={tagChipSx(theme)}
            />
          ))}
          {domains.length === 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Invite-only access is active.
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Cross-app connector: Org Admin opt-out for the conductor bridge that
          carries work between PowerPoint, Excel, and Word. On by default; this
          writes entitlements.conductor on the org. Org Admin only. */}
      {s.isOrgAdmin && (
        <Paper elevation={0} sx={{ ...consoleCardSx(theme), mt: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Cross-app connector</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Let Restack carry work between PowerPoint, Excel, and Word. On by default.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={conductorChecked}
                  disabled={togglingConductor}
                  onChange={(e) => toggleConductor(e.target.checked)}
                  inputProps={{ 'aria-label': 'Cross-app connector' }}
                />
              }
              label={togglingConductor ? 'Saving…' : (conductorChecked ? 'On' : 'Off')}
              labelPlacement="start"
            />
          </Stack>
        </Paper>
      )}

      {s.isOrgAdmin && (
        <Paper elevation={0} sx={{ ...consoleCardSx(theme), mt: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Route Claude through opencode</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Use the shared agent loop for ordinary Claude turns. Turn off to use the Claude Agent SDK.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Switch
                  checked={claudeViaOpencodeChecked}
                  disabled={togglingClaudeViaOpencode}
                  onChange={(e) => toggleClaudeViaOpencode(e.target.checked)}
                  inputProps={{ 'aria-label': 'Route Claude through opencode' }}
                />
              )}
              label={togglingClaudeViaOpencode ? 'Saving…' : (claudeViaOpencodeChecked ? 'On' : 'Off')}
              labelPlacement="start"
            />
          </Stack>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Switching this organization back to the Claude Agent SDK does not merge conversation history. Users must start a new chat after switching.
          </Alert>
        </Paper>
      )}

      <Dialog open={!!roleMember} onClose={savingRoles ? undefined : () => setRoleMember(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Manage member roles</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Choose the administrative access for {roleMember?.email || roleMember?.name || 'this member'}. A member with neither role can use Restack but cannot manage the organization.
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={roleDraft.includes('orgAdmin')} onChange={() => toggleRoleDraft('orgAdmin')} />}
              label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Organization admin</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Manage members, roles, firm settings, and organization access.</Typography>
                </Box>
              )}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={roleDraft.includes('billingAdmin')}
                  disabled={editingBillingContact}
                  onChange={() => toggleRoleDraft('billingAdmin')}
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Billing admin</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Manage seats, credit top-ups, payment methods, and invoices.</Typography>
                </Box>
              )}
            />
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: '8px' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Billing contact</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Owns Restack billing settings and must retain Billing Admin access.
                </Typography>
              </Box>
              {editingBillingContact ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Current contact"
                  sx={{ ...statusChipSx(theme, 'success'), alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={savingRoles || roleMember?.status === 'deactivated'}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                  onClick={() => {
                    setBillingContactCandidate(roleMember);
                    setRoleMember(null);
                  }}
                >
                  Make billing contact
                </Button>
              )}
            </Stack>
          </Paper>
          <Alert severity="info" sx={{ mt: 2 }}>
            Restack prevents removing the final organization admin. Transfer the billing contact before removing that person's billing access.
          </Alert>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setRoleMember(null)} disabled={savingRoles}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={saveRoles}
            disabled={savingRoles}
            startIcon={savingRoles ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {savingRoles ? 'Saving…' : 'Save roles'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!billingContactCandidate} onClose={transferringContact ? undefined : () => setBillingContactCandidate(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Transfer billing contact?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {billingContactCandidate?.name || billingContactCandidate?.email || 'This member'} will become Restack's billing contact and receive Billing Admin access. The current contact keeps their existing roles until you change them separately. This does not change Stripe's invoice email, which is managed in the billing portal.
          </Typography>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx} data-testid="billing-contact-actions">
          <Button onClick={() => setBillingContactCandidate(null)} disabled={transferringContact}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={transferBillingContact}
            disabled={transferringContact}
            startIcon={transferringContact ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {transferringContact ? 'Transferring…' : 'Transfer billing contact'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deactivateMember} onClose={deactivating ? undefined : () => setDeactivateMember(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Free this organization seat?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This deactivates the Restack organization license for {deactivateMember?.email || deactivateMember?.name || 'this member'} and removes their organization roles. Their Office files are not deleted. Any separate project membership is not changed by this action.
          </Typography>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setDeactivateMember(null)} disabled={deactivating}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={deactivating}
            startIcon={deactivating ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={async () => {
              const member = deactivateMember;
              const ok = await s.deactivateMember(member.userId, member.email || member.name);
              if (ok) setDeactivateMember(null);
            }}
          >
            {deactivating ? 'Freeing seat…' : 'Free license seat'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!domainToRemove} onClose={removingDomain ? undefined : () => setDomainToRemove(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Remove domain claim?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Remove {domainToRemove} from this organization. Existing members remain active, and new members must use an email invitation.
          </Typography>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setDomainToRemove(null)} disabled={removingDomain}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={removingDomain}
            startIcon={removingDomain ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={removeDomain}
          >
            {removingDomain ? 'Removing…' : 'Remove domain'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!inviteToCancel} onClose={cancellingInvite ? undefined : () => setInviteToCancel(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Cancel this invitation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {inviteToCancel} will no longer be able to claim the reserved seat from this invitation. The seat becomes available immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx} data-testid="cancel-invitation-actions">
          <Button onClick={() => setInviteToCancel(null)} disabled={cancellingInvite}>Keep invitation</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={cancellingInvite}
            startIcon={cancellingInvite ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={async () => {
              const email = inviteToCancel;
              const ok = await s.cancelInvite(email);
              if (ok) setInviteToCancel(null);
            }}
          >
            {cancellingInvite ? 'Cancelling…' : 'Cancel invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteOpen} onClose={sendingInvite ? undefined : closeInvite} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Invite a member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            They sign in to re-stack.com with this email and the seat is one click to claim.
          </Typography>
          <Typography component="label" htmlFor="org-invite-email" sx={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
            Work email
          </Typography>
          <TextField
            id="org-invite-email"
            size="small" fullWidth autoFocus placeholder="name@firm.com" type="email"
            value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            disabled={sendingInvite || seatsLeft <= 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inviteEmail && seatsLeft > 0 && !sendingInvite) {
                e.preventDefault();
                sendInvite();
              }
            }}
          />
          {seatsLeft <= 0 && (
            <Typography variant="caption" sx={{ color: 'warning.main', mt: 1, display: 'block' }}>
              No seats available. Add a seat in Billing or deactivate a member first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={closeInvite} disabled={sendingInvite}>Cancel</Button>
          <Button
            variant="contained" disableElevation
            disabled={sendingInvite || !inviteEmail || seatsLeft <= 0}
            startIcon={sendingInvite ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={sendInvite}
          >
            {sendingInvite ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function CreditsTab({ s }) {
  const theme = useTheme();
  const canManageBilling = s.canManageBilling ?? (s.isOrgAdmin || s.isBillingAdmin);
  const billingVerified = !!s.billing && !s.billingError;
  const startingTopUp = isActionPending(s, 'topUp');
  const p = s.creditPool || {};
  const exhausted = p.poolState === 'exhausted';
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);
  // "Pool this cycle" capacity = what's LEFT plus what's been used this cycle.
  // This recovers each org's real available total (it self-corrects for
  // pro-rated mid-cycle signups and rollover) instead of assuming the flat
  // monthly refill, so the gauge stays consistent with "Credits left" above.
  const used = p.burnThisCycle || 0;
  const remaining = p.balance || 0;
  const cycleAvailable = remaining + used;
  const pctUsed = cycleAvailable > 0 ? Math.min(100, (used / cycleAvailable) * 100) : 0;
  const pctRemaining = Math.max(0, 100 - Math.round(pctUsed));
  // Composition of the live balance: monthly credits vs the top-up reserve.
  const subCredits = p.subscriptionCredits || 0;
  const preCredits = p.prepaidCredits || 0;
  return (
    <Box>
      {/* Plain words, no jargon: what's left, what comes back, what we've used.
          (Dropped "projected runway" — a soft guess dressed as a number;
          "Credits left" already answers "are we okay".) */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <StatCard
          label="Credits left"
          value={Number(p.balance || 0).toLocaleString()}
          sub={exhausted ? 'Used up — top up to keep working' : 'available now'}
          accent={exhausted ? theme.palette.warning.main : undefined}
        />
        <StatCard label="Refills each month" value={Number(p.monthlyRefresh || 0).toLocaleString()} sub={p.bonusPoolPct ? `incl. +${Math.round(p.bonusPoolPct * 100)}% bonus` : ''} />
        <StatCard label="Used this month" value={Number(p.burnThisCycle || 0).toLocaleString()} />
      </Stack>

      {/* "Pool this cycle": the screenshot-3 capacity treatment, on the one
          surface where credit numbers are allowed (admin, role-gated). Used vs
          this cycle's allotment, plus an honest composition of the live balance
          (monthly vs top-up). No fabricated limit, no time series. */}
      <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Pool this cycle</Typography>
        {cycleAvailable > 0 ? (
          <>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: exhausted ? 'warning.main' : 'text.primary' }}>
              {pctRemaining}% remaining
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pctUsed}
              sx={{
                height: 8,
                borderRadius: 4,
                my: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                ...(exhausted && { '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.warning.main } }),
              }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {used.toLocaleString()} of {cycleAvailable.toLocaleString()} credits used
              </Typography>
              {date(p.cycleEndsAt) && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Refreshes {date(p.cycleEndsAt)}</Typography>
              )}
            </Stack>
            {preCredits > 0 && subCredits + preCredits > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <Box sx={{ flex: subCredits, backgroundColor: theme.palette.primary.main }} />
                  <Box sx={{ flex: preCredits, backgroundColor: alpha(theme.palette.primary.main, 0.35) }} />
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: theme.palette.primary.main }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>Monthly {subCredits.toLocaleString()}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: alpha(theme.palette.primary.main, 0.35) }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>Top-up {preCredits.toLocaleString()}</Typography>
                  </Stack>
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No credits in the pool yet.</Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Top up the pool</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Top-up credits are $1 each and carry forward 12 months. Monthly credits are spent first.
        </Typography>
        {canManageBilling ? (
          <Stack spacing={1}>
            {!billingVerified && (
              <Alert severity="warning">
                Top-ups are unavailable until billing status is verified. Refresh the console and try again.
              </Alert>
            )}
            {/* Both packs lock while a checkout is being created (one top-up at
                a time), and the pack being purchased says so. */}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={startingTopUp || !billingVerified} onClick={() => s.topUp('booster200')}>
                {isActionPending(s, 'topUp', 'booster200') ? 'Starting…' : 'Add 200 ($200)'}
              </Button>
              <Button variant="contained" disableElevation disabled={startingTopUp || !billingVerified} onClick={() => s.topUp('booster500')}>
                {isActionPending(s, 'topUp', 'booster500') ? 'Starting…' : 'Add 500 ($500)'}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Only an Organization Admin or Billing Admin can purchase top-ups.</Typography>
        )}
      </Paper>

      {/* Per-member usage — folded in from the old Usage tab. Same screen as
          the pool, since "how many credits, and who used them" is one question.
          Numbers only, never content. */}
      <MemberUsageList s={s} />
    </Box>
  );
}

// Per-member credit consumption this cycle (metadata only). Rendered inside the
// Credits tab; no longer its own nav section.
function MemberUsageList({ s }) {
  const theme = useTheme();
  const sorted = [...s.members].sort((a, b) => (b.creditsConsumedThisCycle || 0) - (a.creditsConsumedThisCycle || 0));
  const max = Math.max(1, ...sorted.map((m) => m.creditsConsumedThisCycle || 0));
  const totalUsed = sorted.reduce((acc, m) => acc + (m.creditsConsumedThisCycle || 0), 0);
  return (
    <Paper elevation={0} sx={consoleCardSx(theme)}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>Used by member</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Credits each member used this month. Numbers only, never project content, file names, or prompts.
      </Typography>
      <Stack spacing={1.5}>
        {sorted.map((m, i) => (
          <Box key={m.userId}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{i + 1}. {m.name || m.email || m.userId}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {totalUsed > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${Math.round(((m.creditsConsumedThisCycle || 0) / totalUsed) * 100)}%`}
                    sx={mutedChipSx(theme)}
                  />
                )}
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Number(m.creditsConsumedThisCycle || 0).toLocaleString()}
                </Typography>
              </Stack>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((m.creditsConsumedThisCycle || 0) / max) * 100)}
              sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
            />
          </Box>
        ))}
        {sorted.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>No usage recorded yet this cycle.</Typography>}
      </Stack>
    </Paper>
  );
}

export function BillingTab({ s }) {
  const theme = useTheme();
  const canManageBilling = s.canManageBilling ?? (s.isOrgAdmin || s.isBillingAdmin);
  const b = s.billing || {};
  const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
  const invoiced = b.paymentMethod === 'invoice';
  // Plan label: prefer org.plan (the negotiated tier LABEL, e.g. 'practice')
  // over b.plan (the shared billing ENGINE, always 'pro') so a Practice buyer
  // sees "Practice", not "Pro".
  const planLabel = (s.org?.plan || b.plan || 'pro') === 'practice' ? 'Practice' : 'Pro';
  const seatsUsed = s.org?.memberCount || 0;
  const seatsTotal = b.purchasedSeats || s.org?.purchasedSeats || 0;
  const adjustingSeats = isActionPending(s, 'adjustSeats');
  const openingPortal = isActionPending(s, 'openPortal');

  // Is there a subscription at all? The summary is well formed even when no
  // Stripe subscription exists: the backend answers with its "no subscription"
  // sentinel (status 'free', zero seats, no period, no upcoming charge, no
  // invoices). Any one real signal means there IS a subscription to describe.
  const hasSubscription = Boolean(
    b.stripeSubscriptionId
    || (b.status && b.status !== 'free' && b.status !== 'none')
    || Number(b.purchasedSeats) > 0
    || b.currentPeriodEnd
    || b.upcomingChargeUSD != null
    || (Array.isArray(b.invoices) && b.invoices.length > 0)
  );

  // Seat changes are money: never charge straight off a click. The stepper
  // sets a TARGET locally (2 -> 5 is one move, not three); "Review change"
  // previews the single prorated amount, then one confirm commits one Stripe
  // update = one invoice. No per-click charges.
  const currentSeats = b.purchasedSeats || s.org?.purchasedSeats || 0;
  const SEAT_MIN = Math.max(1, Number(s.org?.memberCount || 0) + (s.pendingInvites?.length || 0));
  // Cap the stepper at the org's ceiling (5 self-serve Pro; a negotiated
  // Practice org runs higher) — never below the current count. 6+ is
  // sales-assisted, so beyond the cap is "talk to us". The backend enforces the
  // true ceiling regardless.
  const SEAT_MAX = Math.max(SEAT_MIN, currentSeats, Number(b.maxSeats) || 5);
  const [seatTarget, setSeatTarget] = useState(currentSeats);
  const [seatPreview, setSeatPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmingSeat, setConfirmingSeat] = useState(false);
  // Keep the stepper synced to the live count when it loads/changes and no
  // change is mid-flight.
  useEffect(() => {
    if (!seatPreview) setSeatTarget(Math.max(currentSeats, SEAT_MIN));
  }, [currentSeats, SEAT_MIN, seatPreview]);
  const seatDelta = seatTarget - currentSeats;
  const requestSeatChange = async () => {
    setPreviewLoading(true);
    try {
      const data = await s.previewSeatChange(seatTarget);
      const hasLockedQuote = Number.isSafeInteger(data?.quoteAmountMinor)
        && typeof data?.quoteCurrency === 'string'
        && Number.isSafeInteger(data?.quoteProrationDate);
      if (data?.delta > 0 && (
        data?.proratedAmountDueUSD == null
        || data?.exactAmountAvailable === false
        || !hasLockedQuote
      )) {
        throw new Error('An exact charge preview is unavailable. No seat change was made. Please try again.');
      }
      if (data?.delta !== 0 && typeof data?.operationId !== 'string') {
        throw new Error('A safe seat operation could not be prepared. No seat change was made. Please try again.');
      }
      if (data?.delta === 0) {
        setSeatPreview(null);
        setSeatTarget(data.newSeats ?? currentSeats);
        await s.refresh();
        return;
      }
      setSeatPreview(data || null);
    } catch (e) {
      s.setError(e.message || 'Could not preview the seat change');
    } finally {
      setPreviewLoading(false);
    }
  };
  const confirmSeatChange = async () => {
    if (!seatPreview) return;
    const hasLockedQuote = Number.isSafeInteger(seatPreview.quoteAmountMinor)
      && typeof seatPreview.quoteCurrency === 'string'
      && Number.isSafeInteger(seatPreview.quoteProrationDate);
    if (seatPreview.delta > 0 && (
      seatPreview.proratedAmountDueUSD == null
      || seatPreview.exactAmountAvailable === false
      || !hasLockedQuote
    )) {
      s.setError('An exact charge preview is required before adding seats. Refresh and try again.');
      return;
    }
    if (typeof seatPreview.operationId !== 'string') {
      s.setError('A safe seat operation identifier is required. Refresh and review the change again.');
      return;
    }
    setConfirmingSeat(true);
    try {
      const ok = await s.adjustSeats({
        targetSeats: seatPreview.targetSeats ?? seatPreview.newSeats,
        expectedCurrentSeats: seatPreview.expectedCurrentSeats ?? seatPreview.currentSeats,
        expectedOperationId: seatPreview.operationId,
        expectedQuoteAmountMinor: seatPreview.quoteAmountMinor,
        expectedQuoteCurrency: seatPreview.quoteCurrency,
        expectedQuoteProrationDate: seatPreview.quoteProrationDate,
      });
      if (ok) setSeatPreview(null);
    } finally {
      setConfirmingSeat(false);
    }
  };

  // Unavailable: the billing source could not be verified. Restack asserts
  // nothing about the plan. Kept strictly separate from the absence state
  // below, which is a verified answer of "there is no subscription".
  if (s.billingError || !s.billing) {
    return (
      <Alert
        severity="warning"
        action={<Button color="inherit" size="small" onClick={() => s.refresh()} disabled={s.loading}>Retry</Button>}
      >
        Billing details are temporarily unavailable. Restack has not assumed a plan status, invoice state, or payment method. Money-changing actions are disabled until this data is verified.
      </Alert>
    );
  }

  // None: billing loaded fine, there is simply no subscription behind it. The
  // normal card would claim a plan, an active status, a seat total, a card on
  // file, and offer a seat preview against a subscription that does not exist,
  // so render the absence honestly and expose no money-changing control.
  if (!hasSubscription) {
    return (
      <Box>
        <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>No active subscription</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Nothing is being billed for this organization.
              </Typography>
            </Box>
            <Chip size="small" variant="outlined" label="No plan" sx={statusChipSx(theme, 'neutral')} />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            There are no purchased seats, no renewal date, no upcoming charge, and no payment method on file.
            Seat changes and the billing portal stay unavailable until a plan is active.
          </Typography>
          {canManageBilling ? (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" disableElevation onClick={() => s.navigate('/pricing')}>
                Choose a plan
              </Button>
              <Button
                variant="outlined"
                component="a"
                href="mailto:hi@re-stack.com?subject=Restack%20plan%20for%20my%20firm"
              >
                Talk to us
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
              Ask an Organization Admin or Billing Admin to start a plan.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Plan summary, the screenshot-1 "license at a glance" card: plan +
          status + renewal up top, then seat fullness and the next charge.
          Replaces the old Plan/Seats/Upcoming-charge stat tiles (this card
          carries all three, without the redundancy). */}
      <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{planLabel} plan</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {b.currentPeriodEnd ? `Renews ${date(b.currentPeriodEnd)}` : 'Active'} · {b.billingCycle || 'monthly'} · {invoiced ? 'invoiced' : 'card'}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={b.status || 'active'}
            sx={{ ...statusChipSx(theme, statusTone(b.status)), textTransform: 'capitalize' }}
          />
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
          <Typography variant="body2">Seats in use</Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{seatsUsed} / {seatsTotal}</Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={seatsTotal ? Math.min(100, (seatsUsed / seatsTotal) * 100) : 0}
          sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.75 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Next charge</Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {money(b.upcomingChargeUSD)}{b.currentPeriodEnd ? ` · ${date(b.currentPeriodEnd)}` : ''}
          </Typography>
        </Stack>
      </Paper>

      {canManageBilling && (
        <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Seats</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {invoiced
              ? 'Removing seats lowers access immediately, with no mid-cycle refund. The new recurring charge applies at renewal. Contact support to add seats so access is granted only after the invoice is paid.'
              : 'Adding seats is prorated and billed now. Removing seats lowers access immediately, with no mid-cycle refund. The new recurring charge applies at renewal, and future monthly credit grants use the new seat count.'}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0} alignItems="center" sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', overflow: 'hidden' }}>
              <IconButton size="small" disabled={adjustingSeats || seatTarget <= SEAT_MIN} onClick={() => setSeatTarget((t) => Math.max(SEAT_MIN, t - 1))} sx={{ borderRadius: 0 }} aria-label="Fewer seats">
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ minWidth: 44, textAlign: 'center', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{seatTarget}</Typography>
              <IconButton size="small" disabled={adjustingSeats || seatTarget >= SEAT_MAX || (invoiced && seatTarget >= currentSeats)} onClick={() => setSeatTarget((t) => Math.min(SEAT_MAX, t + 1))} sx={{ borderRadius: 0 }} aria-label="More seats">
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {seatDelta === 0 ? `${currentSeats} seat${currentSeats === 1 ? '' : 's'} now` : `from ${currentSeats} → ${seatTarget}`}
            </Typography>
            <Button
              variant="contained" disableElevation
              disabled={adjustingSeats || previewLoading || seatDelta === 0 || (invoiced && seatDelta > 0)}
              onClick={requestSeatChange}
            >
              Review change
            </Button>
            {previewLoading && <CircularProgress size={16} />}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            Minimum {SEAT_MIN}: active members and pending invitations each reserve a seat.
          </Typography>
          {invoiced && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              Need another seat? <Box component="a" href="mailto:hi@re-stack.com?subject=Add%20Restack%20Practice%20seats" sx={{ color: 'primary.main', fontWeight: 600 }}>Contact support</Box> for a paid-invoice change.
            </Typography>
          )}
          {seatTarget >= SEAT_MAX && (s.org?.plan || b.plan) !== 'practice' && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1.25, display: 'block' }}>
              Need more than {SEAT_MAX} seats?{' '}
              <Box component="a" href="mailto:hi@re-stack.com?subject=Restack%20pricing%20for%20a%20larger%20team" sx={{ color: 'primary.main', fontWeight: 600 }}>Talk to us</Box>
              {' '}about a negotiated rate, annual terms, or invoicing.
            </Typography>
          )}
        </Paper>
      )}

      {/* Payment method: display state here, manage in Stripe's portal.
          Restack's local billing-contact role does not control Stripe's
          invoice delivery email. */}
      <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Payment method</Typography>
        {invoiced ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Invoiced, net 30. Invoices go to the billing email configured in Stripe. Changing
            Restack's billing contact does not change invoice delivery. To update it, contact <Box component="a" href="mailto:hi@re-stack.com" sx={{ color: 'primary.main' }}>hi@re-stack.com</Box>.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: canManageBilling ? 1.5 : 0 }}>
              Card on file with Stripe. Update the card, billing email, or download past
              invoices in the billing portal.
            </Typography>
            {canManageBilling && (
              <Button size="small" variant="outlined" disabled={openingPortal} onClick={() => s.openPortal()} endIcon={<OpenInNewIcon fontSize="small" />}>
                {openingPortal ? 'Opening…' : 'Manage payment method'}
              </Button>
            )}
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={{ ...consoleCardSx(theme, { padded: false }), overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Invoices</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead sx={tableHeadSx}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(b.invoices || []).map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell>{date(inv.createdAt)}</TableCell>
                  <TableCell>{money(inv.amountUSD)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={inv.status} variant="outlined" sx={{ ...statusChipSx(theme, statusTone(inv.status)), textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell align="right">
                    {inv.hostedInvoiceUrl && (
                      <IconButton
                        size="small"
                        component="a"
                        href={inv.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open invoice from ${date(inv.createdAt)}`}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(!b.invoices || b.invoices.length === 0) && (
                <TableRow><TableCell colSpan={4}><Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>No invoices yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!seatPreview} onClose={confirmingSeat ? undefined : () => setSeatPreview(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>
          {seatPreview && seatPreview.delta < 0
            ? `Remove ${Math.abs(seatPreview.delta)} seat${Math.abs(seatPreview.delta) === 1 ? '' : 's'}`
            : `Add ${seatPreview ? seatPreview.delta : ''} seat${seatPreview && seatPreview.delta === 1 ? '' : 's'}`}
        </DialogTitle>
        <DialogContent>
          {seatPreview && (() => {
            const isRemoval = seatPreview.delta < 0;
            const hasExactAddPreview = isRemoval || (
              seatPreview.proratedAmountDueUSD != null && seatPreview.exactAmountAvailable !== false
            );
            const prorated = isRemoval ? 0 : seatPreview.proratedAmountDueUSD;
            const proratedDisplay = prorated != null
              ? `$${Math.abs(Number(prorated)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : null;
            const recurringDelta = Number(seatPreview.monthlyDeltaUSD || 0);
            const recurringDeltaDisplay = `$${Math.abs(recurringDelta).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
            const currentRecurringDisplay = `$${Number(seatPreview.currentMonthlyUSD || 0).toLocaleString()}`;
            const nextRecurringDisplay = `$${Number(seatPreview.newMonthlyUSD || 0).toLocaleString()}`;
            const recurringChangeCopy = recurringDelta < 0
              ? `The recurring amount decreases by ${recurringDeltaDisplay}/mo, from ${currentRecurringDisplay}/mo to ${nextRecurringDisplay}/mo, at renewal.`
              : recurringDelta > 0
                ? `The recurring amount increases by ${recurringDeltaDisplay}/mo, from ${currentRecurringDisplay}/mo to ${nextRecurringDisplay}/mo, at renewal.`
                : `The recurring amount remains ${nextRecurringDisplay}/mo at renewal.`;
            return (
              <Box>
                {(seatPreview.currentSeats != null && seatPreview.newSeats != null) && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    Your organization goes from <strong>{seatPreview.currentSeats}</strong> to <strong>{seatPreview.newSeats}</strong> seat{seatPreview.newSeats === 1 ? '' : 's'}.
                  </Typography>
                )}
                <Box sx={{ p: 1.5, borderRadius: '8px', border: `1px solid ${theme.palette.divider}`, backgroundColor: alpha(theme.palette.text.primary, 0.02) }}>
                  <Typography sx={microLabelSx}>
                    {isRemoval ? 'Charge or refund today' : 'Seat adjustment charged now'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                    {proratedDisplay || 'Exact preview unavailable'}
                  </Typography>
                  {!isRemoval && hasExactAddPreview && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                      {seatPreview.quoteCurrency}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {isRemoval
                      ? `The seat count is reduced immediately. There is no mid-cycle refund. ${recurringChangeCopy} Future monthly credit grants use the new seat count.`
                      : hasExactAddPreview
                        ? `Stripe invoices the prorated seat adjustment now. The amount above excludes unrelated renewal and pending invoice items. After payment succeeds, the recurring amount becomes ${nextRecurringDisplay}/mo and the seat credits are added.`
                        : 'Restack cannot add seats until Stripe provides an exact charge preview.'}
                  </Typography>
                </Box>
                {!hasExactAddPreview && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    No seat change will be made. Close this dialog and try again.
                  </Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={responsiveDialogActionsSx}>
          <Button onClick={() => setSeatPreview(null)} disabled={confirmingSeat}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={confirmSeatChange}
            disabled={confirmingSeat || (
              seatPreview?.delta > 0
              && (seatPreview?.proratedAmountDueUSD == null || seatPreview?.exactAmountAvailable === false)
            )}
          >
            {confirmingSeat ? 'Processing…' : (seatPreview && seatPreview.delta < 0 ? 'Confirm removal' : 'Confirm and pay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Human labels for the audit trail. admin_action rows are keyed by their
// `action`; everything else is keyed by the event `type`. Anything unmapped
// falls back to a humanized version of the raw key (see humanizeKey).
const ACTION_LABELS = {
  // admin_action payload.action
  role_changed: 'Role changed',
  billing_contact_changed: 'Billing contact changed',
  seats_changed: 'Seats changed',
  member_invited: 'Member invited',
  invite_cancelled: 'Invite cancelled',
  member_removed: 'Member removed',
  domains_changed: 'Domains changed',
  conductor_toggled: 'Cross-app toggled',
  claude_opencode_routing_toggled: 'Claude routing changed',
  firmstyle_updated: 'Firm style updated',
  firmtemplate_updated: 'Firm template updated',
  firmtemplate_removed: 'Firm template removed',
  org_provisioned: 'Org provisioned',
  member_joined: 'Member joined',
  pool_topup_initiated: 'Top-up started',
  // event.type
  database_query: 'Database queried',
  connector_invocation: 'Connector called',
  credential_read: 'Credential accessed',
  credential_write: 'Credential stored',
  credential_rotate: 'Credential rotated',
  credential_delete: 'Credential deleted',
  conductor_message: 'Cross-app message',
  conductor_discovery: 'Cross-app discovery',
  conductor_artifact: 'Artifact shared',
  locker_query: 'Locker search',
  locker_document: 'Locker document',
};

// Fallback label: turn a raw key like "connector_invocation" into
// "Connector invocation" so an unmapped event still reads as English.
function humanizeKey(key) {
  if (!key) return 'Activity';
  const spaced = String(key).replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : 'Activity';
}

// Relative time for the audit feed. Recent events read as "just now / Nm ago /
// Nh ago"; anything past a day falls back to a localized date. The cell carries
// the absolute timestamp as a title so the exact time is always recoverable.
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// CSV exports are commonly opened in Excel. Prefix formula-shaped values with
// an apostrophe so actor or audit metadata cannot be evaluated as a formula.
export function encodeCsvCell(value) {
  const raw = String(value ?? '');
  const spreadsheetSafe = /^\s*[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return `"${spreadsheetSafe.replace(/"/g, '""')}"`;
}

// Enterprise activity trail: metadata-only events recorded by the current audit
// producers, including actor and timestamp, with no document content. Reads via
// the role-gated getOrgAuditLog Cloud Function and pages with a cursor.
export function AuditLogTab({ s }) {
  const theme = useTheme();
  const events = Array.isArray(s.auditEvents) ? s.auditEvents : [];
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const visibleEvents = events.filter((event) => {
    if (!normalizedQuery) return true;
    const key = event.type === 'admin_action' ? event.payload?.action : event.type;
    const label = ACTION_LABELS[key] || humanizeKey(key);
    return [event.actorEmail, event.actorUid, label, key, JSON.stringify(event.payload || {})]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
  });
  const exportCsv = () => {
    const rows = visibleEvents.map((event) => {
      const key = event.type === 'admin_action' ? event.payload?.action : event.type;
      const label = ACTION_LABELS[key] || humanizeKey(key);
      return [event.atIso, event.actorEmail || event.actorUid || 'System', label, JSON.stringify(event.payload || {})]
        .map(encodeCsvCell).join(',');
    });
    const csv = ['"Timestamp","Actor","Action","Metadata"', ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `restack-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  // Load the first page on mount (only if it has not loaded and is not already
  // in flight). Further pages come from the "Load more" button.
  useEffect(() => {
    if (!s.auditLoaded && !s.auditLoading) s.loadAuditLog({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      {s.auditError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={<Button color="inherit" size="small" onClick={() => s.loadAuditLog({ reset: true })}>Retry</Button>}
        >
          {s.auditError}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by actor, action, or metadata"
          inputProps={{ 'aria-label': 'Filter activity' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<DownloadRoundedIcon />}
          disabled={visibleEvents.length === 0}
          onClick={exportCsv}
        >
          Export CSV
        </Button>
      </Stack>
      {/* No in-card header: the section shell already renders the "Activity"
          title and the identical description line, so repeating them here was
          double-shown copy. The page description carries the metadata-only
          promise. */}
      <TableContainer component={Paper} elevation={0} sx={consoleCardSx(theme, { padded: false })}>
        <Table size="small">
          <TableHead sx={tableHeadSx}>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleEvents.map((event, i) => {
              // admin_action carries its verb in payload.action; everything else
              // is identified by its type. The chip label maps that key through
              // ACTION_LABELS, falling back to a humanized form.
              const isAdminAction = event.type === 'admin_action';
              const key = isAdminAction ? event.payload?.action : event.type;
              const label = ACTION_LABELS[key] || humanizeKey(key);
              const sev = (event.severity || '').toUpperCase();
              const isCredential = typeof event.type === 'string' && event.type.startsWith('credential_');
              const warn = isCredential || sev === 'WARNING' || sev === 'ERROR';
              // Details = the payload's metadata, minus the `action` key (already
              // shown as the chip). Scalars render inline; objects/arrays get a
              // short JSON.stringify so a row never spills content.
              const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};
              const detailEntries = Object.entries(payload).filter(([k]) => k !== 'action');
              return (
                <TableRow key={event.id || `${event.atIso || ''}-${i}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Tooltip title={event.atIso ? new Date(event.atIso).toLocaleString() : ''}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {relativeTime(event.atIso)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">{event.actorEmail || event.actorUid || 'System'}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Chip
                      size="small"
                      label={label}
                      variant="outlined"
                      sx={warn ? statusChipSx(theme, 'warning') : statusChipSx(theme, 'neutral')}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {detailEntries.length === 0 ? (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {detailEntries.map(([k, v]) => {
                          const display = (v !== null && typeof v === 'object')
                            ? JSON.stringify(v).slice(0, 60)
                            : String(v);
                          return (
                            <Chip
                              key={k}
                              size="small"
                              label={`${k}: ${display}`}
                              variant="outlined"
                              sx={{ ...mutedChipSx(theme), fontVariantNumeric: 'tabular-nums' }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {/* First-page load: skeleton rows shaped like real events, so the
                table doesn't jump from empty to filled. */}
            {s.auditLoading && events.length === 0 && [0, 1, 2, 3].map((i) => (
              <TableRow key={`skeleton-${i}`}>
                <TableCell><Skeleton variant="text" width={64} /></TableCell>
                <TableCell><Skeleton variant="text" width={150} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={110} height={22} sx={{ borderRadius: '6px' }} /></TableCell>
                <TableCell><Skeleton variant="text" width={180} /></TableCell>
              </TableRow>
            ))}
            {!s.auditLoading && visibleEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    {normalizedQuery ? 'No activity matches this filter.' : 'No activity recorded yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {s.auditCursor && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disableElevation
            disabled={s.auditLoading}
            onClick={() => s.loadAuditLog()}
          >
            Load more
          </Button>
        </Stack>
      )}
    </Box>
  );
}

// Test-only harness shell (the live app routes the tab components flat inside
// the Settings console). Usage folded into Credits, so it is not a tab.
const TABS = [
  { key: 'seats', label: 'Members', icon: <GroupRoundedIcon fontSize="small" /> },
  { key: 'credits', label: 'Credits', icon: <BoltRoundedIcon fontSize="small" /> },
  { key: 'billing', label: 'Billing', icon: <ReceiptLongRoundedIcon fontSize="small" /> },
];

export default function AdminScreen() {
  const theme = useTheme();
  const s = useAdminScreen();
  // ?welcome=1 lands here straight from checkout. Read once; dismissing strips
  // the param so a refresh does not resurrect the checklist.
  const [showWelcome, setShowWelcome] = useState(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('welcome') === '1'
  );
  const dismissWelcome = () => {
    setShowWelcome(false);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  if (s.accessLoading || (s.loading && s.canAccessAdmin && !s.org)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!s.canAccessAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Admin access required</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          The Admin panel is for Org Admins and Billing Admins. If you manage your firm's Restack account, ask an existing admin to grant you a role.
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => s.navigate('/settings')}>Back to Settings</Button>
      </Container>
    );
  }

  if (isCoreOrgLoadBlocked(s)) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <CoreOrgLoadErrorState s={s} />
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }} data-theme="light">
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.5 }}>
            <IconButton onClick={() => s.navigate('/settings')} size="small"><ArrowBackIcon /></IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{s.org?.name || 'Organization'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Admin · {(s.org?.plan === 'practice' ? 'Practice' : 'Pro')} · {s.org?.memberCount || 0} of {s.org?.purchasedSeats || 0} seats
              </Typography>
            </Box>
            {s.org?.status && <Chip size="small" label={s.org.status} variant="outlined" sx={{ ...statusChipSx(theme, statusTone(s.org.status)), textTransform: 'capitalize' }} />}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {s.error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => s.setError(null)}>{s.error}</Alert>}
        {s.actionMsg && <Alert severity={s.actionSeverity || 'success'} sx={{ mb: 2 }}>{s.actionMsg}</Alert>}

        {showWelcome && <WelcomeChecklist s={s} onDismiss={dismissWelcome} />}

        <Tabs
          value={s.tab}
          onChange={(_, v) => s.setTab(v)}
          sx={{ mb: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {TABS.map((t) => <Tab key={t.key} value={t.key} icon={t.icon} iconPosition="start" label={t.label} sx={{ minHeight: 48, textTransform: 'none', fontWeight: 600 }} />)}
        </Tabs>

        {s.busy && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {s.tab === 'seats' && <SeatsTab s={s} />}
        {s.tab === 'credits' && <CreditsTab s={s} />}
        {s.tab === 'billing' && <BillingTab s={s} />}
      </Container>
    </Box>
  );
}
